import {
  WSActions,
  WSCard,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import { useWSMutation } from "@ws-react-query";
import {
  GetMissingFieldsLevelParameter,
  StakeholderSubjectType
} from "@wingspanhq/wingspan-api-sdk";
import { useState } from "react";
import { ErrorAlert } from "@components/ErrorAlert";
import { complianceEntitiesApi, stakeholdersApi } from "@api";
import {
  queriesComplianceEntities,
  queriesInternalAccounts,
  queriesOnboarding,
  queriesStakeholders
} from "@query";
import { useSession } from "@hooks/useSession";
import { DataView } from "../../../../shared/components/DataView";
import { getLast4 } from "../../../../shared/utils/getLast4";
import { getTaxIdLabel } from "../../../../shared/utils/taxId";
import { ConfirmationDisclosures } from "../ConfirmationDisclosures";
import { ReviewPerson } from "../ReviewPerson";
import { upsertStakeholderIdentity } from "../../steps/CreateAccount/useMutationSubmit";
import { FormDataAdditionalInfoRepresentative } from "./FormAdditionalInfoRepresentative";

type Props = {
  formData?: FormDataAdditionalInfoRepresentative;
  onBack?: () => void;
  onNext?: () => void;
  country?: string;
};

export const Review: React.FC<Props> = props => {
  const [consent, setConsent] = useState(false);
  const session = useSession();
  const accountId = session.accountId;
  const country = props.country || "US";

  const [submit, meta] = useWSMutation(
    async () => {
      if (!props.formData?.representative) {
        throw new Error("No representative set");
      }

      const stakeholders = await stakeholdersApi.listAccountStakeholders({
        accountId
      });
      const existingStakeholders = stakeholders.data ?? [];

      // 1. Representative (Principal): the identity already exists from account
      // creation, so update the ownership stake and add the tax id. Tax
      // identifiers are add-only — read the entity first to skip a registration
      // that already exists (re-adding it 409s).
      const principal = existingStakeholders.find(
        stakeholder =>
          stakeholder.subjectType === StakeholderSubjectType.PERSON &&
          stakeholder.isPrincipal
      );
      if (principal?.id) {
        await stakeholdersApi.updateStakeholder({
          accountId,
          stakeholderId: principal.id,
          stakeholderUpdate: {
            ownershipPercentage:
              props.formData.representative.ownershipStake ?? 0,
            isController: true
          }
        });

        const representativeTaxId = props.formData.representative.taxId;
        if (
          representativeTaxId &&
          principal.accountSuppliedComplianceEntityId
        ) {
          const taxIdType = country === "US" ? "SSN" : "TIN";
          const entity = await complianceEntitiesApi.getComplianceEntity({
            complianceEntityId: principal.accountSuppliedComplianceEntityId
          });
          const alreadyPresent = (entity.taxIdentifiers ?? []).some(
            taxId =>
              taxId.hasValue &&
              taxId.countryCode === country &&
              taxId.type === taxIdType
          );
          if (!alreadyPresent) {
            await complianceEntitiesApi.updateComplianceEntity({
              complianceEntityId: principal.accountSuppliedComplianceEntityId,
              complianceEntityUpdate: {
                taxIdentifiers: [
                  {
                    countryCode: country,
                    type: taxIdType,
                    value: representativeTaxId
                  }
                ]
              }
            });
          }
        }
      }

      // 2. Beneficial owners: create-or-update each stakeholder and its
      // compliance entity. `occupation` and `phoneNumber` have no v3 home and
      // are dropped (PLA-3241); `dateOfBirth` is only set at create time.
      for (const beneficialOwner of props.formData?.beneficialOwners ?? []) {
        await upsertStakeholderIdentity({
          accountId,
          country,
          existing: existingStakeholders.find(
            stakeholder =>
              stakeholder.subjectType === StakeholderSubjectType.PERSON &&
              !stakeholder.isPrincipal &&
              stakeholder.email === beneficialOwner.email
          ),
          subjectType: StakeholderSubjectType.PERSON,
          person: beneficialOwner
        });
      }
    },
    {
      onSuccess: props.onNext,
      dependencies: [
        queriesOnboarding.getOnboardingStatus.queryKeyBase,
        queriesOnboarding.getMissingFields.queryKeyBase,
        queriesComplianceEntities.listComplianceEntities.queryKeyBase,
        queriesStakeholders.listAccountStakeholders.queryKeyBase,
        queriesInternalAccounts.listInternalAccounts.queryKeyBase
      ]
    }
  );

  return (
    <WSList gap="2XL">
      <WSCard header="Review & submit">
        <WSText.ParagraphSm color="gray600">
          Please review the information you provided for accuracy and submit to
          continue.
        </WSText.ParagraphSm>
      </WSCard>

      <DataView
        title="Representative information (You)"
        onEdit={props.onBack}
        data={[
          {
            label: getTaxIdLabel("Individual", props.country || "US"),
            value:
              !!props.formData?.representative?.taxId &&
              typeof props.formData?.representative?.taxId === "string"
                ? `••• ${getLast4(props.formData.representative.taxId)}`
                : undefined,
            fsExclude: true
          },
          {
            label: "Ownership stake",
            value: props.formData?.representative?.ownershipStake
              ? `${props.formData?.representative?.ownershipStake}%`
              : undefined
          }
        ]}
      />

      {props.formData?.beneficialOwners?.map((owner, index) => (
        <ReviewPerson
          key={index}
          type="BeneficialOwner"
          formData={owner}
          onEdit={props.onBack}
        />
      ))}

      <WSCard>
        <ConfirmationDisclosures
          consent={consent}
          setConsent={setConsent}
          verificationLevel={GetMissingFieldsLevelParameter.BANKING}
        />
      </WSCard>

      <ErrorAlert context="MonitoredEntity" error={meta.error} />

      <WSActions
        alignment="fill"
        buttons={[
          { label: "Back", onClick: props.onBack, kind: "Secondary" },
          {
            label: "Confirm & continue",
            disabled: !consent,
            loading: meta.isLoading,
            onClick: submit
          }
        ]}
      />
    </WSList>
  );
};
